import type { RootState } from '@generalTypes/rootStateTypes';
import { closeTeaserModal, type Teaser } from '@newStore/documentUI/documentUIState';
import React, { useEffect, useState } from 'react';
import {
  Modal,
  KovResourcePicker,
  AsyncKovResourcePicker,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import { contentApi } from '@store/api/apiConfig';
import { useDispatch, useSelector } from 'react-redux';
import type { ContentHref } from '@generalTypes/apiTypes';
import { formatDate } from '@newStore/genericHelpers';
import { escapeRegExp } from 'lodash';

interface Theme {
  $$meta: {
    permalink: string;
  };
  display: string;
  href: ContentHref;
}

export const TeaserModal: React.FC = () => {
  const dispatch = useDispatch();
  const [teaser, setTeaser] = useState<Teaser | null>(null);
  const [teasers, setTeasers] = useState<string[] | null>(null);
  const [themes, setThemes] = useState<Theme[]>([]);

  const { open, parentHref } = useSelector((state: RootState) => state.documentUI.teaserModal);
  const referenceFrameThemes = useSelector(
    (state: RootState) => state.document.viewModel.referenceFrameThemes,
  );
  const levelParent = useSelector((state: RootState) =>
    state.document.viewModel.flatWithHiddens.find((z) => z.key === parentHref?.split('/').pop()),
  );

  useEffect(() => {
    const filteredThemes = referenceFrameThemes?.options?.filter((r) =>
      levelParent?.themes?.includes(r.$$meta.permalink),
    );
    setThemes(filteredThemes ?? []);
  }, [referenceFrameThemes, levelParent]);

  const issuedAfter = React.useMemo(() => {
    const olderThan6Months = new Date();
    olderThan6Months.setMonth(olderThan6Months.getMonth() - 6);
    return olderThan6Months.toISOString();
  }, []);

  const sriLoadOptions = useLoadSriOptions({
    sriClient: contentApi,
    href: '/content',
    parameters: {
      type: 'TEASER',
      themesOverlaps: teasers,
      issuedAfter,
    },
  });


  if (!open) return null;

  const handleSetTheme = (updatedThemes: Theme[]) => {
    setThemes(updatedThemes);

    if (updatedThemes.length === 0) {
      // deleting all themes provides all teasers
      setTeasers(null);
    } else {
      const themeHrefs = [
        ...updatedThemes.reduce((themesMap, theme) => {
          themesMap.add(theme.$$meta.permalink);
          const recursiveChildren =
            referenceFrameThemes.itemsRecursiveChildren.get(
              theme.$$meta.permalink,
            );
          recursiveChildren?.forEach((t) => themesMap.add(t.href));
          return themesMap;
        }, new Set<string>()),
      ];
      setTeasers(themeHrefs);
    }

  };

  const handleCancel = () => {
    dispatch(closeTeaserModal(null));
  };

  const handleSubmit = () => {
    if (teaser) {
      dispatch(closeTeaserModal(teaser));
    }
  };

  const display = (item: { issued: string, title: string }) => {
    const issued = item.issued ? formatDate(item.issued) : 'CONCEPT';
    return `${issued} - ${item.title}`;
  }

  const filterData = (item: { data: Teaser }, str: string) => {
    if (str.length === 0) {
      return false;
    }
    const regExp = new RegExp(escapeRegExp(str), 'gi');
    return regExp.test(item.data.$$expanded.title);
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title="Teaser invoegen" onClose={handleCancel} />
      <Modal.Body>
        <div className="form-group">
          <label className="control-label">Teasers met thema</label>
          <div className="row">
            <div className="col-sm-12">
              <KovResourcePicker
                defaultValue={themes}
                value={themes}
                localData={referenceFrameThemes.options}
                display={(o) => o.display}
                multiple={true}
                clearable={true}
                disableSearch={true}
                menuPlacement="bottom"
                onChange={(t: Theme[]) => handleSetTheme(t)}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-sm-12">
              <AsyncKovResourcePicker
                value={teaser}
                loadOptions={sriLoadOptions}
                clearable={true}
                localData={true}
                filter={filterData}
                onChange={(teaser: Teaser) => setTeaser(teaser)}
                placeholder="Selecteer een item"
                multiple={false}
                menuPlacement="bottom"
                optionValue={(o) => o.$$expanded.key}
                optionTemplate={(o) => display(o.$$expanded)}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={handleCancel}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!teaser}>
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

TeaserModal.displayName = 'TeaserModal';

export default TeaserModal;